import { PermissionsContext } from "../permission-context";
import { useContext, useEffect, useState } from "react";
import dScribeJobsService from "../api/services/dScribeJobs-service";
import { showErrorToast } from "../helpers/utils";
import { Box, Typography, Skeleton, Switch, FormControlLabel } from "@mui/material"; // Added Switch imports
import Grid from "@mui/material/Grid2";
import unauth from "../images/unauth.png";
import SharedTable from "../components/shared-table";
import { Popover } from "antd";
import { TooltipCell } from "../components/shared-table";

export default function DeviceInfo() {
  const { loading, error, hasPermission } = useContext(PermissionsContext);
  const [showTableSkeleton, setSkeleton] = useState(true);
  const [deviceInfo, setDeviceInfo] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [hideDeveloperRecords, setHideDeveloperRecords] = useState(true);

  const fetchData = async () => {
    try {
      setSkeleton(true);
      const res = await dScribeJobsService.getVscribeAiDeviceInfo();
      const data = Array.isArray(res.data) ? res.data : [];
      setDeviceInfo(data);
    } catch (error) {
      console.log(error);
      showErrorToast("Failed to load device data. Please try again.");
    } finally {
      setSkeleton(false);
    }
  };

  useEffect(() => {
    fetchData();
    const handleResize = () => {
      // Standard Mobile/Tablet Breakpoint
      setIsSmallScreen(window.innerWidth < 768);
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const appNameFilters = [...new Set(deviceInfo.map((item) => item.appName).filter(Boolean))].map(
    (name) => ({
      text: name,
      value: name,
    })
  );

  const columns = [
    {
      title: "User Id",
      dataIndex: "userId",
      fixed: "left",
      width: isSmallScreen ? 120 : 180,
      sorter: (a, b) => a.userId?.localeCompare(b.userId),
      render: (text) => {
        const contentStyle = {
          width: isSmallScreen ? "100px" : "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: isSmallScreen ? "pointer" : "default",
          color: isSmallScreen ? "#1890ff" : "inherit",
        };

        return (
          <Popover
            content={
              <div style={{ maxWidth: "300px", wordBreak: "break-all", color: "white" }}>
                {text}
              </div>
            }
            trigger={isSmallScreen ? "click" : "hover"}
            color='rgba(0, 0, 0, 0.85)'>
            <div style={contentStyle}>{text}</div>
          </Popover>
        );
      },
    },
    {
      title: "Account Id",
      dataIndex: "accountId",
      width: 120,
      sorter: (a, b) => a.accountId?.localeCompare(b.accountId),
      render: (text) => <TooltipCell text={text} width='100px' />,
    },
    {
      title: "Platform",
      dataIndex: "platform",
      width: 120,
      render: (text) => <span style={{ textTransform: "capitalize" }}>{text}</span>,
    },
    {
      title: "App Name",
      dataIndex: "appName",
      width: 150,
      sorter: (a, b) => a.appName?.localeCompare(b.appName),
      filters: appNameFilters,
      onFilter: (value, record) => record.appName === value,
    },
    {
      title: "App Version",
      dataIndex: "appVersionString",
      width: 120,
      sorter: (a, b) =>
        a.appVersionString?.localeCompare(b.appVersionString, undefined, { numeric: true }),
    },
    {
      title: "Last Seen",
      dataIndex: "lastSeenAt",
      width: 180,
      sorter: (a, b) => new Date(a.lastSeenAt) - new Date(b.lastSeenAt),
      render: (text) =>
        text
          ? new Date(text).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "numeric",
            })
          : "-",
    },
    {
      title: "Package Name",
      dataIndex: "appPackageName",
      width: 200,
      render: (text) => <TooltipCell text={text} width='170px' />,
    },
    {
      title: "Device Name",
      dataIndex: "deviceName",
      width: 180,
      render: (text) => <TooltipCell text={text} width='150px' />,
    },
    {
      title: "Type",
      dataIndex: "deviceType",
      width: 120,
    },
    {
      title: "Idiom",
      dataIndex: "deviceIdiom",
      width: 120,
    },
    {
      title: "Model",
      dataIndex: "model",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "Device Version",
      dataIndex: "deviceVersionString",
      width: 130,
    },
  ];

  const filteredRows = hideDeveloperRecords
    ? deviceInfo.filter((r) => {
        const account = r.accountId?.toUpperCase() || "";
        return account !== "TD" && account !== "MHC";
      })
    : deviceInfo;

  const finalColumns =
    filteredRows.length === 0 ? columns.map((col) => ({ ...col, fixed: undefined })) : columns;

  return (
    <>
      {!loading && !error && hasPermission("AppDeviceInfo") && (
        <Box style={{ marginRight: "1%", marginLeft: "2%" }}>
          <Grid
            container
            alignItems='center'
            justifyContent='space-between'
            sx={{
              marginTop: 1.5,
              borderBottom: 1,
              borderColor: "#dfdfdf",
            }}>
            <Grid>
              <Typography
                gutterBottom
                fontWeight='700'
                lineHeight={"31.25px"}
                fontSize={"24px"}
                fontStyle={"DM Sans"}>
                Application Device information
              </Typography>
            </Grid>
            <Grid>
              <FormControlLabel
                control={
                  <Switch
                    id='hide-developer-records'
                    checked={hideDeveloperRecords}
                    onChange={(e) => setHideDeveloperRecords(e.target.checked)}
                    color='primary'
                    sx={{ transform: "scale(0.75)" }}
                  />
                }
                label={
                  <Typography
                    variant='body2'
                    fontWeight='500'
                    fontStyle='DM Sans'
                    color='text.secondary'>
                    Hide Developer Records
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          <div
            style={{
              margin: "12px 0",
              fontFamily: "DM Sans",
              minHeight: "60vh",
            }}>
            {showTableSkeleton ? (
              <Skeleton
                variant='rectangular'
                width='100%'
                height='60vh'
                sx={{ borderRadius: "15px" }}
                animation='wave'
              />
            ) : (
              <SharedTable
                columns={finalColumns}
                dataSource={filteredRows}
                scroll={{ x: "max-content" }}
                tableName='deviceInfo'
                rowKey={(record) =>
                  `${record.userId}_${record.accountId}_${record.deviceIdentifier}`
                }
              />
            )}
          </div>
        </Box>
      )}
      {!loading && !error && !hasPermission("AppDeviceInfo") && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "90vh",
          }}>
          <img src={unauth} alt='UnAuthorized' style={{ maxWidth: "100%", maxHeight: "100%" }} />
        </Box>
      )}
    </>
  );
}
